/**
 * Proyecto: MOGOTAX
 * Módulo TAI - Gestión de Transportadoras
 * Fecha: 18 de Febrero 2026
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import {
  Table,
  Button,
  Input,
  Space,
  Pagination,
  Tooltip,
  Spin,
  message,
  Select,
  Row,
  Col,
  Dropdown,
  Tag,
  Card,
  Statistic,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  PlusOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import ViewTransportadoraDialog from "./ViewTransportadoraDialog";
import DeleteTransportadoraDialog from "./DeleteTransportadoraDialog";
import TransportadoraForm from "./TransportadoraForm";
import { useUser } from "../../../UserContext";

const { Search } = Input;
const { Option } = Select;

const TIPOS_TRANSPORTE = [
  { value: "bus", label: "Bus" },
  { value: "taxi", label: "Taxi" },
  { value: "especial", label: "Especial" },
  { value: "mixto", label: "Mixto" },
];

const ESTADOS = [
  { value: "activo", label: "Activo" },
  { value: "inactivo", label: "Inactivo" },
];

const Transportadoras = () => {
  const [transportadoras, setTransportadoras] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedTransportadora, setSelectedTransportadora] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transportadoraToDelete, setTransportadoraToDelete] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [loadingTransportadora, setLoadingTransportadora] = useState(false);
  const { user: currentUser } = useUser();

  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key?.toLowerCase() === "transportadoras");
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo?.toLowerCase() === "transportadoras" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Función para obtener transportadoras
  const fetchTransportadoras = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {};
      if (selectedEstado) params.estado = selectedEstado;
      if (selectedTipo) params.tipo_transporte = selectedTipo;
      if (selectedZona) params.zona_id = selectedZona;

      const response = await api.get("/transportadoras", { params });
      setTransportadoras(response.data);
    } catch (error) {
      console.error("Error al obtener las transportadoras:", error);
      message.error("Error al obtener las transportadoras");
    } finally {
      setListLoading(false);
    }
  }, [selectedEstado, selectedTipo, selectedZona]);

  // Función para obtener zonas
  const fetchZonas = useCallback(async () => {
    setLoadingZonas(true);
    try {
      const response = await api.get("/transportadoras/zonas-disponibles");
      setZonas(response.data);
    } catch (error) {
      console.error("Error al obtener las zonas:", error);
    } finally {
      setLoadingZonas(false);
    }
  }, []);

  // Función para obtener estadísticas
  const fetchEstadisticas = useCallback(async () => {
    try {
      const response = await api.get("/transportadoras/estadisticas");
      setEstadisticas(response.data);
    } catch (error) {
      console.error("Error al obtener estadísticas:", error);
    }
  }, []);

  useEffect(() => {
    fetchTransportadoras();
    fetchZonas();
    fetchEstadisticas();
  }, [fetchTransportadoras, fetchZonas, fetchEstadisticas]);

  useEffect(() => {
    setPage(1);
  }, [filter, selectedZona, selectedTipo, selectedEstado]);

  const handleSearchChange = (event) => {
    setFilter(event.target.value);
    setPage(1);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (current, size) => {
    setRowsPerPage(size);
    setPage(1);
  };

  const handleEditClick = useCallback(async (transportadora) => {
    try {
      setLoadingTransportadora(true);
      const response = await api.get(`/transportadoras/${transportadora.id}`);
      setSelectedTransportadora(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener la transportadora:", error);
      message.error("Error al obtener los datos de la transportadora");
    } finally {
      setLoadingTransportadora(false);
    }
  }, []);

  const handleViewClick = useCallback(async (transportadora) => {
    try {
      setLoadingTransportadora(true);
      const response = await api.get(`/transportadoras/${transportadora.id}`);
      setSelectedTransportadora(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener la transportadora:", error);
      message.error("Error al obtener los datos de la transportadora");
    } finally {
      setLoadingTransportadora(false);
    }
  }, []);

  const handleOpenDeleteDialog = useCallback((transportadora) => {
    setTransportadoraToDelete(transportadora);
    setDeleteDialogOpen(true);
  }, []);

  const handleRefresh = () => {
    fetchTransportadoras();
    fetchEstadisticas();
  };

  const clearFilters = () => {
    setFilter("");
    setSelectedZona(null);
    setSelectedTipo(null);
    setSelectedEstado(null);
  };

  const filteredTransportadoras = useMemo(() => {
    return transportadoras.filter((t) => {
      const matchesFilter =
        t.nombre?.toLowerCase().includes(filter.toLowerCase()) ||
        t.nit?.toLowerCase().includes(filter.toLowerCase()) ||
        t.razon_social?.toLowerCase().includes(filter.toLowerCase()) ||
        t.email?.toLowerCase().includes(filter.toLowerCase());

      return matchesFilter;
    });
  }, [transportadoras, filter]);

  const paginatedTransportadoras = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredTransportadoras.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTransportadoras, page, rowsPerPage]);

  const getTipoColor = (tipo) => {
    const colors = {
      bus: "blue",
      taxi: "gold",
      especial: "purple",
      mixto: "green",
    };
    return colors[tipo] || "default";
  };

  const columns = useMemo(
    () => [
      {
        title: "Nombre",
        dataIndex: "nombre",
        key: "nombre",
        sorter: (a, b) => a.nombre.localeCompare(b.nombre),
        render: (text, record) => (
          <Space>
            <CarOutlined />
            <span style={{ fontWeight: 500 }}>{text}</span>
            {record.es_mogotax_especial && (
              <Tag color="orange" size="small">
                Mogotax
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: "NIT",
        dataIndex: "nit",
        key: "nit",
        sorter: (a, b) => (a.nit || "").localeCompare(b.nit || ""),
      },
      {
        title: "Tipo",
        dataIndex: "tipo_transporte",
        key: "tipo_transporte",
        render: (tipo) => (
          <Tag color={getTipoColor(tipo)}>
            {tipo?.charAt(0).toUpperCase() + tipo?.slice(1)}
          </Tag>
        ),
        sorter: (a, b) =>
          (a.tipo_transporte || "").localeCompare(b.tipo_transporte || ""),
      },
      {
        title: "Teléfono",
        dataIndex: "telefono",
        key: "telefono",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Zona",
        dataIndex: "zona",
        key: "zona",
        render: (zona) => zona?.nombre || "Sin Zona",
        sorter: (a, b) =>
          (a.zona?.nombre || "").localeCompare(b.zona?.nombre || ""),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        render: (estado) =>
          estado === "activo" ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Activo
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">
              Inactivo
            </Tag>
          ),
        sorter: (a, b) => a.estado.localeCompare(b.estado),
      },
      {
        title: "Acciones",
        key: "acciones",
        render: (text, transportadora) => {
          const items = [
            hasPermission("VISUALIZAR_TR") && {
              key: "ver",
              icon: <EyeOutlined />,
              label: (
                <Tooltip title="Ver Transportadora">
                  <span>Ver Transportadora</span>
                </Tooltip>
              ),
              onClick: () => handleViewClick(transportadora),
            },
            hasPermission("EDITAR_TR") && {
              key: "editar",
              icon: <EditOutlined />,
              label: (
                <Tooltip title="Editar Transportadora">
                  <span>Editar Transportadora</span>
                </Tooltip>
              ),
              onClick: () => handleEditClick(transportadora),
            },
            hasPermission("ELIMINAR_TR") && {
              key: "eliminar",
              icon: <DeleteOutlined />,
              label: (
                <Tooltip title="Eliminar Transportadora">
                  <span>Eliminar Transportadora</span>
                </Tooltip>
              ),
              onClick: () => handleOpenDeleteDialog(transportadora),
            },
          ].filter(Boolean);

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<EllipsisOutlined />}
                  type="text"
                  style={{ padding: 0 }}
                />
              </Dropdown>
            </div>
          );
        },
        width: 100,
      },
    ],
    [handleEditClick, handleViewClick, handleOpenDeleteDialog, hasPermission]
  );

  const tableHeaderStyle = {
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
    boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.4)",
  };

  return (
    <div className="transportadoras-container">
      {/* Estadísticas */}
      {estadisticas && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total"
                value={estadisticas.total}
                prefix={<CarOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Activas"
                value={estadisticas.activas}
                valueStyle={{ color: "#3f8600" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Inactivas"
                value={estadisticas.inactivas}
                valueStyle={{ color: "#cf1322" }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Mogotax Especial"
                value={estadisticas.mogotax_especial}
                valueStyle={{ color: "#fa8c16" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Barra de herramientas */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space wrap>
            <Search
              placeholder="Buscar transportadora"
              enterButton={<SearchOutlined />}
              value={filter}
              onChange={handleSearchChange}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              placeholder="Tipo"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => setSelectedTipo(value)}
              value={selectedTipo}
            >
              {TIPOS_TRANSPORTE.map((tipo) => (
                <Option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Estado"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => setSelectedEstado(value)}
              value={selectedEstado}
            >
              {ESTADOS.map((estado) => (
                <Option key={estado.value} value={estado.value}>
                  {estado.label}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Zona"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => setSelectedZona(value)}
              loading={loadingZonas}
              value={selectedZona}
            >
              {zonas.map((zona) => (
                <Option key={zona.id} value={zona.id}>
                  {zona.nombre}
                </Option>
              ))}
            </Select>
            <Button onClick={clearFilters}>Limpiar</Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Actualizar
            </Button>
            {hasPermission("CREAR_TR") && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddDialogOpen(true)}
              >
                Agregar Transportadora
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Tabla */}
      <Spin spinning={listLoading} tip="Cargando transportadoras...">
        <Table
          columns={columns.map((col) => ({
            ...col,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
          }))}
          dataSource={paginatedTransportadoras}
          pagination={false}
          rowKey="id"
          bordered
          scroll={{ y: 500 }}
        />
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Pagination
            current={page}
            pageSize={rowsPerPage}
            total={filteredTransportadoras.length}
            onChange={handleChangePage}
            showSizeChanger
            onShowSizeChange={handleRowsPerPageChange}
            pageSizeOptions={["5", "10", "25", "50", "100"]}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} de ${total} transportadoras`
            }
          />
        </div>
      </Spin>

      {/* Diálogos */}

      {/* Editar Transportadora */}
      {selectedTransportadora && editDialogOpen && hasPermission("EDITAR_TR") && (
        <Spin spinning={loadingTransportadora} tip="Cargando...">
          <TransportadoraForm
            key={`edit-${selectedTransportadora.id}`}
            initialData={selectedTransportadora}
            open={editDialogOpen}
            handleClose={() => {
              setEditDialogOpen(false);
              setSelectedTransportadora(null);
            }}
            fetchTransportadoras={() => {
              fetchTransportadoras();
              fetchEstadisticas();
            }}
            zonas={zonas}
            isEdit={true}
          />
        </Spin>
      )}

      {/* Ver Transportadora */}
      {viewDialogOpen && hasPermission("VISUALIZAR_TR") && (
        <Spin spinning={loadingTransportadora} tip="Cargando...">
          <ViewTransportadoraDialog
            transportadora={selectedTransportadora}
            open={viewDialogOpen}
            handleClose={() => {
              setViewDialogOpen(false);
              setSelectedTransportadora(null);
            }}
          />
        </Spin>
      )}

      {/* Eliminar Transportadora */}
      {deleteDialogOpen && hasPermission("ELIMINAR_TR") && (
        <DeleteTransportadoraDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setTransportadoraToDelete(null);
          }}
          transportadora={transportadoraToDelete}
          fetchTransportadoras={() => {
            fetchTransportadoras();
            fetchEstadisticas();
          }}
        />
      )}

      {/* Agregar Transportadora */}
      {addDialogOpen && hasPermission("CREAR_TR") && (
        <TransportadoraForm
          key="add-transportadora"
          open={addDialogOpen}
          handleClose={() => setAddDialogOpen(false)}
          fetchTransportadoras={() => {
            fetchTransportadoras();
            fetchEstadisticas();
          }}
          zonas={zonas}
          isEdit={false}
        />
      )}
    </div>
  );
};

export default Transportadoras;
